import React, { useState, useMemo } from 'react';
import { ROXO, TIPO, ETAPAS, MESES, hoje, somaDias, fmtPeriodo, intervalo } from './demandasUtil';

// Planejamento anual das demandas: ano (12 meses) → mês (grade de dias) → dia (lista).
// A demanda ocupa o período (início/fim) ou, sem período, o dia do prazo.

const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const pad = (n) => String(n).padStart(2, '0');
const ultimoDia = (a, m) => new Date(Date.UTC(a, m + 1, 0)).getUTCDate(); // m 0-based
const isoDe = (a, m, d) => `${a}-${pad(m + 1)}-${pad(d)}`;
const diasEntre = (a, b) => Math.round((new Date(`${b}T12:00:00Z`) - new Date(`${a}T12:00:00Z`)) / 86400000);
const PERIODO_LONGO = 7; // a partir de 7 dias vira faixa em cima da grade do mês, em vez de repetir em cada dia

function Etiqueta({ d, onAbrir, compacta }) {
  const e = ETAPAS[d.status] || ETAPAS.a_fazer;
  const tipo = TIPO[d.tipo];
  return (
    <div role="button" onClick={(ev) => { ev.stopPropagation(); onAbrir(d); }} title={d.titulo}
      className="rounded px-1 mb-1 text-truncate"
      style={{ background: e.bg, color: e.cor, fontSize: compacta ? 11 : 12, borderLeft: `3px solid ${e.cor}`, textDecoration: d.status === 'cancelada' ? 'line-through' : 'none' }}>
      {tipo && <i className={`bi ${tipo.icon} me-1`} />}{d.titulo}
    </div>
  );
}

export default function CalendarioDemandas({ demandas, onAbrir, onNova, onMover }) {
  const h = hoje();
  const [ano, setAno] = useState(parseInt(h.slice(0, 4)));
  const [mes, setMes] = useState(null); // 0-11
  const [dia, setDia] = useState(null); // 1-31

  const comData = useMemo(() => demandas
    .map(d => ({ d, iv: intervalo(d) }))
    .filter(x => x.iv)
    .sort((a, b) => a.iv.ini.localeCompare(b.iv.ini) || a.d.titulo.localeCompare(b.d.titulo)), [demandas]);
  const semData = demandas.length - comData.length;

  // demandas que tocam o intervalo [ini, fim]
  const noIntervalo = (ini, fim) => comData.filter(x => x.iv.ini <= fim && x.iv.fim >= ini);

  const irMes = (delta) => {
    let m = mes + delta, a = ano;
    if (m < 0) { m = 11; a--; } else if (m > 11) { m = 0; a++; }
    setAno(a); setMes(m);
  };
  const irDia = (delta) => {
    const iso = somaDias(isoDe(ano, mes, dia), delta);
    setAno(parseInt(iso.slice(0, 4))); setMes(parseInt(iso.slice(5, 7)) - 1); setDia(parseInt(iso.slice(8, 10)));
  };

  // ------------------------------------------------------------------ cabeçalho / navegação
  const cab = (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <nav className="d-flex align-items-center gap-1 fw-semibold">
        <button className="btn btn-sm btn-link p-0 fw-semibold" style={{ color: ROXO, textDecoration: 'none' }} onClick={() => { setMes(null); setDia(null); }}>
          <i className="bi bi-calendar3 me-1" />{ano}
        </button>
        {mes !== null && <><i className="bi bi-chevron-right small text-muted" />
          <button className="btn btn-sm btn-link p-0 fw-semibold" style={{ color: dia ? ROXO : '#333', textDecoration: 'none' }} onClick={() => setDia(null)}>{MESES[mes]}</button></>}
        {dia !== null && <><i className="bi bi-chevron-right small text-muted" /><span>{dia}</span></>}
      </nav>
      <div className="d-flex align-items-center gap-1">
        <button className="btn btn-sm btn-outline-mkt" title="Anterior"
          onClick={() => (dia !== null ? irDia(-1) : mes !== null ? irMes(-1) : setAno(a => a - 1))}><i className="bi bi-chevron-left" /></button>
        <button className="btn btn-sm btn-outline-mkt" onClick={() => {
          setAno(parseInt(h.slice(0, 4)));
          if (mes !== null) setMes(parseInt(h.slice(5, 7)) - 1);
          if (dia !== null) setDia(parseInt(h.slice(8, 10)));
        }}>Hoje</button>
        <button className="btn btn-sm btn-outline-mkt" title="Próximo"
          onClick={() => (dia !== null ? irDia(1) : mes !== null ? irMes(1) : setAno(a => a + 1))}><i className="bi bi-chevron-right" /></button>
      </div>
    </div>
  );

  const legenda = (
    <div className="d-flex flex-wrap gap-2 small text-muted mt-3 align-items-center">
      {['planejada', 'a_fazer', 'fazendo', 'feito'].map(s => (
        <span key={s}><span className="d-inline-block rounded-circle me-1" style={{ width: 9, height: 9, background: ETAPAS[s].cor }} />{ETAPAS[s].label}</span>
      ))}
      {semData > 0 && <span className="ms-2">· {semData} demanda{semData > 1 ? 's' : ''} sem data não aparece{semData > 1 ? 'm' : ''} aqui</span>}
    </div>
  );

  // ------------------------------------------------------------------ ANO
  if (mes === null) {
    return (
      <div>
        {cab}
        <div className="row g-3">
          {MESES.map((nome, m) => {
            const itens = noIntervalo(isoDe(ano, m, 1), isoDe(ano, m, ultimoDia(ano, m)));
            const atual = h.slice(0, 7) === `${ano}-${pad(m + 1)}`;
            return (
              <div key={m} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div className="card border-0 shadow-sm h-100" role="button" onClick={() => setMes(m)}
                  style={{ outline: atual ? `2px solid ${ROXO}` : 'none' }}>
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold" style={{ color: ROXO }}>{nome}</span>
                      <span className="badge rounded-pill" style={{ background: itens.length ? '#F5EBF2' : '#f1f1f1', color: itens.length ? ROXO : '#999' }}>{itens.length}</span>
                    </div>
                    {itens.length === 0 && <div className="text-muted small py-2">Nada planejado</div>}
                    {itens.slice(0, 6).map(({ d }) => <Etiqueta key={d.id} d={d} onAbrir={onAbrir} compacta />)}
                    {itens.length > 6 && <div className="small text-muted">+ {itens.length - 6} mais…</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {legenda}
      </div>
    );
  }

  // ------------------------------------------------------------------ DIA
  if (dia !== null) {
    const iso = isoDe(ano, mes, dia);
    const itens = noIntervalo(iso, iso);
    const extenso = new Date(`${iso}T12:00:00Z`).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
    const titulo = extenso.charAt(0).toUpperCase() + extenso.slice(1);
    return (
      <div>
        {cab}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h6 className="mb-0">{titulo}{iso === h && <span className="badge ms-2" style={{ background: ROXO }}>hoje</span>}</h6>
          <button className="btn btn-sm btn-primary-mkt" onClick={() => onNova(iso)}><i className="bi bi-plus-lg me-1" />Nova demanda neste dia</button>
        </div>
        {itens.length === 0 && <div className="card border-0 shadow-sm p-4 text-center text-muted">Nada neste dia.</div>}
        {itens.map(({ d }) => {
          const e = ETAPAS[d.status] || ETAPAS.a_fazer;
          const tipo = TIPO[d.tipo];
          return (
            <div key={d.id} className="card border-0 shadow-sm mb-2" style={{ borderLeft: `4px solid ${e.cor}` }}>
              <div className="card-body p-2 d-flex flex-wrap align-items-center gap-2">
                <div className="flex-grow-1" role="button" onClick={() => onAbrir(d)} style={{ minWidth: 200 }}>
                  <div className="fw-semibold small">{d.titulo}</div>
                  <div className="d-flex flex-wrap gap-1 mt-1" style={{ fontSize: 11 }}>
                    <span className="badge" style={{ background: e.bg, color: e.cor }}><i className={`bi ${e.icon} me-1`} />{e.label}</span>
                    {tipo && <span className="badge" style={{ background: '#F5EBF2', color: ROXO }}><i className={`bi ${tipo.icon} me-1`} />{tipo.label}</span>}
                    {d.data_inicio && <span className="badge bg-light text-dark border"><i className="bi bi-calendar-range me-1" />{fmtPeriodo(d.data_inicio, d.data_fim)}</span>}
                    {d.prazo && <span className="badge bg-light text-dark border"><i className="bi bi-flag me-1" />prazo {fmtPeriodo(d.prazo)}</span>}
                    {d.acoes?.length > 0 && <span className="badge bg-light text-muted border"><i className="bi bi-lightning me-1" />{d.acoes.filter(a => a.ref_id).length}/{d.acoes.length} ações geradas</span>}
                  </div>
                </div>
                {d.status === 'planejada' && (
                  <button className="btn btn-sm btn-outline-mkt" onClick={() => onMover(d, 'a_fazer')} title="Leva a demanda pra coluna A fazer do quadro">
                    <i className="bi bi-box-arrow-in-right me-1" />Puxar pro quadro
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {legenda}
      </div>
    );
  }

  // ------------------------------------------------------------------ MÊS
  const ini = isoDe(ano, mes, 1);
  const fim = isoDe(ano, mes, ultimoDia(ano, mes));
  const doMes = noIntervalo(ini, fim);
  const longos = doMes.filter(x => diasEntre(x.iv.ini, x.iv.fim) + 1 >= PERIODO_LONGO);
  const curtos = doMes.filter(x => diasEntre(x.iv.ini, x.iv.fim) + 1 < PERIODO_LONGO);
  const offset = new Date(`${ini}T12:00:00Z`).getUTCDay();
  const celulas = [...Array(offset).fill(null), ...Array.from({ length: ultimoDia(ano, mes) }, (_, i) => i + 1)];
  while (celulas.length % 7) celulas.push(null);

  return (
    <div>
      {cab}
      {longos.length > 0 && (
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body p-2">
            <div className="small fw-bold text-muted mb-1"><i className="bi bi-calendar-range me-1" />Períodos que passam por {MESES[mes].toLowerCase()}</div>
            <div className="row g-1">
              {longos.map(({ d, iv }) => (
                <div key={d.id} className="col-12 col-md-6 col-xl-4">
                  <div className="d-flex align-items-center gap-1">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}><Etiqueta d={d} onAbrir={onAbrir} /></div>
                    <span className="small text-muted text-nowrap mb-1">{fmtPeriodo(iv.ini, iv.fim)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="card border-0 shadow-sm">
        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {SEMANA.map(s => <div key={s} className="text-center small fw-bold text-muted py-1 border-bottom">{s}</div>)}
          {celulas.map((n, i) => {
            if (!n) return <div key={i} className="border-bottom border-end" style={{ background: '#fafafa', minHeight: 90 }} />;
            const iso = isoDe(ano, mes, n);
            const doDia = curtos.filter(x => x.iv.ini <= iso && x.iv.fim >= iso);
            const ehHoje = iso === h;
            return (
              <div key={i} role="button" onClick={() => setDia(n)} className="border-bottom border-end p-1"
                style={{ minHeight: 90, background: ehHoje ? '#fbf5f9' : '#fff', minWidth: 0 }}>
                <div className="small mb-1" style={{ fontWeight: ehHoje ? 700 : 500, color: ehHoje ? ROXO : '#555' }}>{n}</div>
                {doDia.slice(0, 3).map(({ d }) => <Etiqueta key={d.id} d={d} onAbrir={onAbrir} compacta />)}
                {doDia.length > 3 && <div className="text-muted" style={{ fontSize: 11 }}>+{doDia.length - 3}</div>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="small text-muted mt-2">Clique num dia pra ver tudo o que acontece nele (inclusive os períodos) e criar uma demanda já com a data.</div>
      {legenda}
    </div>
  );
}
